import React, { useState } from 'react';
import { Button, SparkIcon, Spinner, Text, TooltipTrigger, sparkToast } from '@spark';
import { downloadTrainSetVersion } from '@/features/evaluation/api/versionsDiffApi';
import { BigStatValue, StatCard } from './ModelDetailsStatCard';
import ModelDetailsSectionTitle from './ModelDetailsSectionTitle';
import ModelDetailsOriginsSection from './ModelDetailsOriginsSection';
import DistributionTable from './DistributionTable';
import { TaskType, isGenerativeTask } from '@/shared/domain/taskTypes';

function sortByReviewedThenTotal(rows) {
  return [...rows].sort((a, b) => {
    const diff = (b.validated ?? 0) - (a.validated ?? 0);
    if (diff !== 0) return diff;
    return (b.total ?? 0) - (a.total ?? 0);
  });
}

function getDistributionSections(taskType, safeSnapshot) {
  const reviewDepth = sortByReviewedThenTotal(Array.isArray(safeSnapshot.reviewDepth) ? safeSnapshot.reviewDepth : []);
  const entityReviewDepth = sortByReviewedThenTotal(Array.isArray(safeSnapshot.entityReviewDepth) ? safeSnapshot.entityReviewDepth : []);
  const relationsReviewDepth = sortByReviewedThenTotal(Array.isArray(safeSnapshot.relationsReviewDepth) ? safeSnapshot.relationsReviewDepth : []);

  const isNer = taskType === TaskType.NER;
  const isNerWithRelations = taskType === TaskType.NER_WITH_RELATIONS;

  if (isNerWithRelations) {
    return [
      { title: 'Entity distribution and % reviewed', nameColumnHeader: 'Entity Name', rows: entityReviewDepth },
      { title: 'Relations distribution and % reviewed', nameColumnHeader: 'Relation Name', rows: relationsReviewDepth },
    ];
  }
  if (isNer) {
    return [
      { title: 'Entity distribution and % reviewed', nameColumnHeader: 'Entity Name', rows: entityReviewDepth },
    ];
  }
  return [
    { title: 'Label distribution and % reviewed', nameColumnHeader: 'Label Name', rows: reviewDepth },
  ];
}

// Exact-reason formatters, ordered most-to-least common. Each maps a
// `data_reduction` count key to a human sentence shown in the tooltip.
const REDUCTION_REASONS = [
  ['duplicate_dropped', (n) => `${n.toLocaleString()} removed as duplicate ${n === 1 ? 'row' : 'rows'}`],
  ['contamination_dropped', (n) => `${n.toLocaleString()} removed for also appearing in your benchmark`],
  ['invalid_dropped', (n) => `${n.toLocaleString()} removed as empty or invalid ${n === 1 ? 'row' : 'rows'}`],
  ['benchmark_holdout', (n) => `${n.toLocaleString()} held out to create a benchmark`],
];

// Shorter generic fallback for older runs that lack a stored breakdown.
const GENERIC_REDUCTION_TEXT =
  'Some uploaded rows were excluded before training - for example duplicate rows, ' +
  'rows that also appear in your benchmark, or rows held out to build a benchmark.';

// Per-reason breakdown rendered as a bulleted list (one explicit line per
// exclusion type with its count), passed to TooltipTrigger's rich `content`
// slot — mirrors the main-branch implementation.
function buildReductionTooltip(reduction) {
  const reasons = [];
  if (reduction) {
    REDUCTION_REASONS.forEach(([key, format]) => {
      const n = Number(reduction[key]) || 0;
      if (n > 0) reasons.push(format(n));
    });
  }
  if (reasons.length === 0) return GENERIC_REDUCTION_TEXT;
  return (
    <div>
      <div style={{ marginBottom: 4 }}>Not all uploaded rows are used for training:</div>
      <ul style={{ margin: 0, paddingLeft: 16 }}>
        {reasons.map((reason) => (
          <li key={reason}>{reason}</li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Icon-only "Download train set" button shown on the Data Snapshot title row.
 * Downloads the merged train-set snapshot of the shown version — same endpoint
 * and filename convention as the Versions Diff modal's train-set cards.
 */
function TrainSetDownloadButton({ clientId, distillationEventId, versionLabel, taskName }) {
  const [downloading, setDownloading] = useState(false);
  const displayName = `train_set_${versionLabel || 'version'}.json`;
  const downloadName = taskName ? `${taskName}_${displayName}` : displayName;
  const handleDownload = async () => {
    setDownloading(true);
    try {
      await downloadTrainSetVersion({
        client_id: clientId,
        distillation_event_id: distillationEventId,
        fileName: downloadName,
      });
    } catch (error) {
      sparkToast.error(error?.message || 'Failed to download train set');
    } finally {
      setDownloading(false);
    }
  };
  return (
    /* Icon-only Spark Buttons render their own tooltip (from aria-label). */
    <Button
      variant="secondary"
      size="small"
      type="button"
      aria-label="Download train set"
      disabled={downloading}
      icon={downloading ? <Spinner size="small" /> : <SparkIcon slug="download" size="small" />}
      onClick={handleDownload}
    />
  );
}

export default function ModelDetailsDataTab({
  snapshot,
  clientId,
  distillationEventId,
  versionLabel,
  taskName,
}) {
  const safeSnapshot = snapshot || {};
  const datasetOrigins = Array.isArray(safeSnapshot.datasetOrigins)
    ? safeSnapshot.datasetOrigins
    : [];
  const taskType = safeSnapshot.taskType || null;
  const showSecondCard = !isGenerativeTask(taskType);

  // Only surface the "of N uploaded" note when the big number is the actual
  // training-record count and it is genuinely lower than what was uploaded.
  const reduction = safeSnapshot.dataReduction || null;
  const trainingExamples = Number(safeSnapshot.totalExamplesValue) || 0;
  // Prefer the pipeline's own "uploaded" count so it reconciles exactly with the
  // per-reason breakdown; fall back to the analysis example count on older runs.
  const uploadedExamples =
    reduction && Number(reduction.uploaded) > 0
      ? Number(reduction.uploaded)
      : Number(safeSnapshot.uploadedExamplesValue) || 0;
  const showUploadedNote =
    Boolean(safeSnapshot.usesTrainingRecordCount) &&
    uploadedExamples > trainingExamples &&
    trainingExamples > 0;
  const reductionTooltip = showUploadedNote ? buildReductionTooltip(reduction) : null;

  const distributionSections = !isGenerativeTask(taskType)
    ? getDistributionSections(taskType, safeSnapshot)
    : [];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 14,
        }}
      >
        <ModelDetailsSectionTitle style={{ marginBottom: 0 }}>Data Snapshot</ModelDetailsSectionTitle>
        {distillationEventId ? (
          <TrainSetDownloadButton
            clientId={clientId}
            distillationEventId={distillationEventId}
            versionLabel={versionLabel}
            taskName={taskName}
          />
        ) : null}
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: showSecondCard ? '1fr 1fr' : '1fr',
          gap: 14,
          marginBottom: 12,
        }}
      >
        <StatCard label="Total Examples">
          <div style={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', gap: 6 }}>
            <BigStatValue>{safeSnapshot.totalExamples ?? '-'}</BigStatValue>
            {showUploadedNote ? (
              <Text variant="detail-regular" style={{ fontSize: 12 }}>
                out of {uploadedExamples.toLocaleString()}{' '}
                <TooltipTrigger content={reductionTooltip}>
                  <span style={{ color: '#9CA3AF', cursor: 'help', display: 'inline-flex', verticalAlign: 'middle' }}>
                    <SparkIcon slug="circle-help" size="small" />
                  </span>
                </TooltipTrigger>
              </Text>
            ) : null}
          </div>
        </StatCard>

        {showSecondCard ? (
          taskType === TaskType.NER_WITH_RELATIONS ? (
            <StatCard label="Entities & Relations">
              <div style={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', gap: 10 }}>
                <BigStatValue>{safeSnapshot.totalEntities ?? '-'}</BigStatValue>
                <Text variant="detail-regular" style={{ color: '#9CA3AF', fontSize: 12 }}>Entities</Text>
                <BigStatValue>{safeSnapshot.totalRelations ?? '-'}</BigStatValue>
                <Text variant="detail-regular" style={{ color: '#9CA3AF', fontSize: 12 }}>Relations</Text>
              </div>
            </StatCard>
          ) : taskType === TaskType.NER ? (
            <StatCard label="Total Entities">
              <BigStatValue>{safeSnapshot.totalEntities ?? '-'}</BigStatValue>
            </StatCard>
          ) : (
            <StatCard label="Total Labels">
              <BigStatValue>{safeSnapshot.totalLabels ?? '-'}</BigStatValue>
            </StatCard>
          )
        ) : null}
      </div>

      <ModelDetailsOriginsSection title="Data origin" origins={datasetOrigins} />

      {distributionSections.map((section, idx) => (
        <DistributionTable
          key={section.title}
          title={section.title}
          nameColumnHeader={section.nameColumnHeader}
          rows={section.rows}
          flexGrow={idx === distributionSections.length - 1}
        />
      ))}
    </div>
  );
}

